/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { switchMap, map, distinctUntilChanged, filter, catchError } from 'rxjs/operators';
import { EMPTY, Observable, of } from 'rxjs';
import {
	fancyFirebaseGetters,
	fancyFirebaseReferencer,
	FirebaseStateError,
	ProjectCatalogType,
} from '@touchplan/fancy-referencer';
import { AppState } from 'src/app/services/app-state.service';
import { FancyFirebaseState } from '../services/fancy-firebase-state.service';
import { ObservableJar } from 'src/app/plain-javascript/ObservableJar';
import { ILoggerService } from '@touchplan/logging';
import { LoggerServiceFactory } from '../../logging/LoggerServiceFactory';
import {
	PARTITIONING_ERROR_PROJECT_CATALOG_NOT_FOUND,
	PARTITIONING_ERROR_REPARTITION_IN_PROGRESS,
} from '@touchplan/strings';

import firebase from 'firebase/compat/app';

enum AccessLevel {
	Admin = 'Admin',
	General = 'General',
}

export function indexRef(userId: string) {
	const path = fancyFirebaseGetters.getIndicesUsersPath(userId);
	return fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(path);
}

export function accessLevelRef(projectId: string, projectMemberId: string) {
	const path = fancyFirebaseGetters.getProjectMembersPath(projectId, projectMemberId);
	return fancyFirebaseReferencer
		.getLegacyRef<firebase.database.Reference>(path, projectId)
		.child('accessLevel');
}

/**
 * Intended to be long running service (duration of the app) that maintains a constant connection to the projectMember accessLevel's
 * corresponding to your user. This allows near instant querying of adminship.
 * This service very deliberately does not expose any synchronous accessors.
 */

@Injectable({
	providedIn: 'root',
})
export class IsAdminService {
	private started = false;
	private adminshipJar = new ObservableJar((projectId: string) => {
		return this.fetchAdminship(projectId);
	});
	private logger: ILoggerService = LoggerServiceFactory.create('IsAdminService');

	constructor(
		private db: AngularFireDatabase,
		private firebaseState: FancyFirebaseState,
		private appState: AppState
	) {}

	//runs forever
	private monitorForRemoval() {
		this.appState.user
			.pipe(
				map((user) => user?.indices),
				filter((indices) => !!indices)
			)
			.subscribe((indices) => {
				const keys = this.adminshipJar.keys;
				for (const key of keys) {
					if (!indices.has(key)) {
						this.adminshipJar.delete(key);
					}
				}
			});
	}

	//only start once, otherwise no-op
	private tryStart() {
		if (!this.started) {
			this.monitorForRemoval();
			this.started = true;
		}
	}

	private fetchAdminship(projectId: string) {
		return this.firebaseState.getProjectCatalog$(projectId, ProjectCatalogType.ProjectData).pipe(
			catchError((err: FirebaseStateError) => {
				if (
					err?.message === PARTITIONING_ERROR_PROJECT_CATALOG_NOT_FOUND ||
					err?.message === PARTITIONING_ERROR_REPARTITION_IN_PROGRESS
				) {
					this.logger.warn('Expected Firebase Error', err);
					return EMPTY;
				}
				throw err;
			}),
			switchMap(() => this.getProjectMemberPairFromProject$(projectId)),
			switchMap((projectProjectMemberIdPair) =>
				this.requestAdminFromFirebase$(projectId, projectProjectMemberIdPair.projectMemberId)
			)
		);
	}

	private getProjectMemberPairFromProject$(projectId: string) {
		return this.appState.user.pipe(
			map((user) => user?.indices.get(projectId) || { projectId: null, projectMemberId: null })
		);
	}

	private requestAdminFromFirebase$(projectId: string, projectMemberId: string) {
		if (!projectId || !projectMemberId) {
			return of(AccessLevel.General);
		}
		const ref = accessLevelRef(projectId, projectMemberId);
		return this.db.object<AccessLevel>(ref).valueChanges();
		// .pipe(
		//requesting projectMember data you don't have access to, pretend they're general (leave as is for now)
		// catchError(() => of(AccessLevel.General))
		// );
	}

	/**
	 * Emits true/ false if you're an admin on the passed in projectId.
	 * On a component it's preferred to call this method on initialization of the component instead of in a function.
	 * This prevent angular being dumb and constant re-subscribing 30 times a second.
	 */
	public isAdminOnProject$(projectId$: Observable<string>) {
		this.tryStart();

		return this.adminshipJar.get$(projectId$).pipe(
			map((accessLevel) => accessLevel === AccessLevel.Admin),
			distinctUntilChanged()
		);
	}
}
